import { useState } from "react";
import { Modal } from "./ui";
import { Icon } from "./Icons";
import { copilotAsk } from "../lib/ai";
import { projectSnapshot } from "../lib/snapshot";
import type { Project } from "../lib/types";

/* Ask the project copilot — a grounded NL answer over the whole project. */
const SUGGESTIONS = [
  "What's outstanding from the client?",
  "Summarise the fee position and what's still to bill.",
  "What still needs the studio's attention?",
  "Which drawings are undrawn or missing?",
];

export function Copilot({ p }: { p: Project }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn soft" onClick={() => setOpen(true)}><Icon.spark width={16} />Ask copilot</button>
      {open && <CopilotModal p={p} onClose={() => setOpen(false)} />}
    </>
  );
}

function CopilotModal({ p, onClose }: { p: Project; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [thread, setThread] = useState<{ q: string; a: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);

  async function ask(question: string) {
    const query = question.trim();
    if (!query || busy) return;
    setBusy(true); setErr(null); setQ("");
    const res = await copilotAsk(query, projectSnapshot(p));
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The copilot isn't switched on (needs an AI key)." : "Couldn't reach the copilot."); return; }
    setThread((t) => [...t, { q: query, a: res.answer ?? "" }]);
  }

  return (
    <Modal open onClose={onClose} title="Project copilot" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>Ask anything about <b>{p.name}</b> — it answers from the project's own data: drawings, billing, reviews, RFIs and variations.</p>
        {thread.length === 0 && (
          <div className="row wrap" style={{ gap: 6 }}>
            {SUGGESTIONS.map((s) => <button key={s} className="chip" style={{ fontSize: 11.5 }} onClick={() => ask(s)}>{s}</button>)}
          </div>
        )}
        {thread.length > 0 && (
          <div className="col" style={{ gap: 12, maxHeight: "46vh", overflowY: "auto" }}>
            {thread.map((m, i) => (
              <div key={i} className="col" style={{ gap: 6 }}>
                <div className="row" style={{ gap: 8 }}><span className="badge" style={{ fontSize: 9 }}>You</span><span style={{ fontSize: 13.5, fontWeight: 500 }}>{m.q}</span></div>
                <div className="panel" style={{ padding: "10px 13px", whiteSpace: "pre-wrap", fontSize: 13.5, lineHeight: 1.6 }}>{m.a}</div>
              </div>
            ))}
          </div>
        )}
        {busy && <div className="row" style={{ gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />Thinking…</div>}
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        <div className="row" style={{ gap: 8 }}>
          <input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask(q)} placeholder="Ask about this project…" />
          <button className="btn accent" disabled={busy || !q.trim()} onClick={() => ask(q)}><Icon.send width={15} /></button>
        </div>
      </div>
    </Modal>
  );
}
